import React from 'react';

// =============================================================================
// Inbox-provenance badge (CTR-016 / REQ-011 / AC-U-009).
//
// Renders when a row's `source_reference` matches `^inbox_resolution:` — the
// marker set by TaxWriteApiHandler when a manual transaction or manual tax lot
// is created via the combined inbox-resolution endpoint (AD-7).
//
// Marker format (from TASK-009): `inbox_resolution:{portfolioId}:{sourceIdentifier}`.
// The tooltip exposes the parsed `(portfolioId, sourceIdentifier)` tuple — where
// `sourceIdentifier` is the source Coinbase order UUID — so the operator can trace
// the row back to its inbox origin.
//
// CTR-018 AC-2: when the caller supplies the disposal's occurrence date and product
// ID (already present in the consuming view's row data — no extra API call), the
// tooltip gains `Date: YYYY-MM-DD` and `Product: <id>` lines ABOVE the existing UUID
// tuple, so an operator revisiting a prior-year return can identify the source
// transaction without a database query.
//
// Reuses the canonical `.badge` / `.badge-status` chrome — no new style namespace.
// =============================================================================

const MARKER_PREFIX = 'inbox_resolution:';

// Accept either a bare `YYYY-MM-DD` or a full ISO timestamp; surface only the date
// so FR-2's `YYYY-MM-DD` contract holds regardless of how the caller stored the value.
const toYmd = (value: string): string => {
  const match = /^\d{4}-\d{2}-\d{2}/.exec(value);
  return match ? match[0] : value;
};

export const InboxProvenanceBadge: React.FC<{
  sourceReference?: string | null;
  occurrenceDate?: string | null;
  productId?: string | null;
}> = ({ sourceReference, occurrenceDate, productId }) => {
  if (!sourceReference || !sourceReference.startsWith(MARKER_PREFIX)) {
    return null;
  }
  const rest = sourceReference.substring(MARKER_PREFIX.length);
  // Split on the first ':' so source identifiers containing ':' survive.
  const firstColon = rest.indexOf(':');
  const portfolioId = firstColon >= 0 ? rest.substring(0, firstColon) : rest;
  const sourceIdentifier = firstColon >= 0 ? rest.substring(firstColon + 1) : '';

  const lines: string[] = [];
  if (occurrenceDate) {
    lines.push(`Date: ${toYmd(occurrenceDate)}`);
  }
  if (productId) {
    lines.push(`Product: ${productId}`);
  }
  // Existing UUID line (portfolioId, source Coinbase order UUID) stays last.
  lines.push(`(${portfolioId}, ${sourceIdentifier})`);
  const tooltip = lines.join('\n');

  return (
    <span className="badge badge-status" title={tooltip}>
      Inbox-resolved
    </span>
  );
};

export default InboxProvenanceBadge;
